import { ListItemIcon, ListItemText, MenuItem, Stack } from "@mui/material";
import { ReactNode } from "react";

import { NegotiationHelper } from "@/common/helpers/entity/negotiation";
import { useNegotiationAllowedActions } from "@/common/hooks/entity/negotiation/useNegotiationAllowedActions";
import { AppPermission, NegotiationDto } from "@/core/api/generated/v1";

import AuthorizedMenuItem from "../../Auth/AuthorizedMenuItem";
import EntityMenu from "../../EntityMenu/EntityMenu";
import AppIcon from "../../Icons/AppIcon";
import NegotiationDeleteModal from "./DataGrantDeleteModal";
import NegotiationSettingsModal from "./EditNegotiationModal";
import CreateNegotiationProposalModal from "./NegotiationProposal/CreateNegotiationProposalModal";

enum ActionFlags {
  Edit = "Edit",
  CreateNewProposal = "CreateNewProposal",
  Delete = "Delete",
}
const defaultDisplayProps = {
  actions: {
    edit: true,
    createNewProposal: true,
    eventLog: true,
    delete: true,
  },
};

interface TriggersProps {
  entity?: NegotiationDto | null;
  definition: typeof ActionFlags;
  handleClose: () => void;
  startAction: (props: ActionFlags) => void;
}

interface Props {
  entity?: NegotiationDto | null;
  disabled?: boolean;
  onUpdate: ((newValue: NegotiationDto | null | undefined) => void) | undefined;
  onDelete?: (() => void) | undefined;
  displayProps?: Partial<typeof defaultDisplayProps>;
  customActions?: (props: TriggersProps) => ReactNode;
}

export default function NegotiationMenu({
  entity,
  disabled,
  customActions,
  onUpdate,
  onDelete,
  displayProps = defaultDisplayProps,
}: Props) {
  displayProps = {
    ...defaultDisplayProps,
    ...displayProps,
  };

  const allowedActions = useNegotiationAllowedActions(entity?.id);
  const canCreateProposal = NegotiationHelper.canCreateProposal(entity, allowedActions);

  return (
    <EntityMenu
      definition={ActionFlags}
      entity={entity}
      disabled={disabled}
      actionTriggers={({ handleClose, startAction }) => (
        <>
          {customActions &&
            customActions({
              entity,
              definition: ActionFlags,
              handleClose,
              startAction,
            })}
          {displayProps?.actions?.edit && (
            <AuthorizedMenuItem
              permissions={[AppPermission.FleetAppAccess, AppPermission.NegotiationManage]}
              onClick={() => startAction(ActionFlags.Edit)}
            >
              <ListItemIcon>
                <AppIcon of='edit' fontSize='small' />
              </ListItemIcon>
              <ListItemText>Edit</ListItemText>
            </AuthorizedMenuItem>
          )}
          {displayProps?.actions?.createNewProposal && (
            <MenuItem
              disabled={!canCreateProposal}
              onClick={() => startAction(ActionFlags.CreateNewProposal)}
            >
              <ListItemIcon>
                <AppIcon of='add' fontSize='small' />
              </ListItemIcon>
              <ListItemText>Create new proposal</ListItemText>
            </MenuItem>
          )}
          {displayProps?.actions?.delete && (
            <AuthorizedMenuItem
              permissions={[AppPermission.FleetAppAccess, AppPermission.NegotiationManage]}
              onClick={() => startAction(ActionFlags.Delete)}
            >
              <ListItemIcon>
                <AppIcon of='delete' fontSize='small' />
              </ListItemIcon>
              <ListItemText>Delete</ListItemText>
            </AuthorizedMenuItem>
          )}
        </>
      )}
      actionHandlers={({ currentAction, cancelAction, completeAction }) => (
        <Stack>
          {/* Edit modal */}
          {entity && (
            <NegotiationSettingsModal
              negotiation={entity}
              open={currentAction === ActionFlags.Edit}
              onClose={() => cancelAction()}
            />
          )}

          {/* Delete entity */}
          {entity && (
            <NegotiationDeleteModal
              entity={entity}
              open={currentAction === ActionFlags.Delete}
              onClose={() => cancelAction()}
              onDelete={() => {
                onDelete && onDelete();
                completeAction();
              }}
            />
          )}

          {/* Create proposal */}
          {entity && (
            <CreateNegotiationProposalModal
              negotiation={entity}
              open={currentAction === ActionFlags.CreateNewProposal}
              onClose={() => cancelAction()}
              onCreated={() => completeAction()}
            />
          )}
        </Stack>
      )}
    />
  );
}
